import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Tooltip from '../components/Tooltip';
import GA4ConnectionModal from '../components/GA4ConnectionModal';
import DateRangeSelector from '../components/DateRangeSelector';
import FeedbackNotification from '../components/FeedbackNotification';
import usePageMetadata from '../utils/usePageMetadata';
// Removed InsightsFeed import - no longer used
import './Content.css';

const EXCLUDED_CATEGORY_NAMES = ['about us', 'contact us', 'help', 'home', 'features'];

const PLATFORM_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  reddit: 'Reddit',
  threads: 'Threads',
  bluesky: 'Bluesky',
  mastodon: 'Mastodon',
  snapchat: 'Snapchat',
  twitch: 'Twitch'
};

const normalizeCategory = (value) => (value || '').trim().toLowerCase();

const isCategoryAllowed = (value) => {
  if (!value) {
    return false;
  }
  return !EXCLUDED_CATEGORY_NAMES.includes(normalizeCategory(value));
};

const formatPlatformLabel = (platform) => {
  if (!platform) return '';
  return PLATFORM_LABELS[platform] || platform.charAt(0).toUpperCase() + platform.slice(1);
};

const formatDuration = (seconds) => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '00m 00s';
  }
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.round(totalSeconds % 60);
  const minuteLabel = `${minutes.toString().padStart(2, '0')}m`;
  const secondLabel = `${remainingSeconds.toString().padStart(2, '0')}s`;
  return `${minuteLabel} ${secondLabel}`;
};

const friendlyNumber = (value) => {
  if (value === null || value === undefined) {
    return '—';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '—';
  }
  return numeric.toLocaleString();
};

const formatPercent = (ratio, fractionDigits = 0) => {
  if (!Number.isFinite(ratio)) {
    return '—';
  }
  const value = ratio * 100;
  return `${value.toFixed(fractionDigits)}%`;
};

const getEngagementBadgeClass = (ratio) => {
  if (!Number.isFinite(ratio)) {
    return 'neutral';
  }
  const percent = ratio * 100;
  if (percent >= 70) return 'positive';
  if (percent >= 40) return 'neutral';
  return 'negative';
};

const formatPosition = (value) => {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return value.toFixed(1);
};

const Content = () => {
  usePageMetadata({
    title: 'Content Dashboard | The Ad Shop',
    description: 'Analyze page performance, content sections, and search queries with unified analytics.'
  });

  const navigate = useNavigate();
  const { token } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [overview, setOverview] = useState(null);
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState(30); // Default to Past 30 days
  const [selectedSection, setSelectedSection] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'pageviews', direction: 'desc' });
  const [showGA4Modal, setShowGA4Modal] = useState(false);
  const [isEditingRss, setIsEditingRss] = useState(false);
  const [rssInput, setRssInput] = useState('');
  const [savingRss, setSavingRss] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncError, setSyncError] = useState(null);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [viewMode, setViewMode] = useState('pages'); // 'pages' or 'queries'
  const [clearing, setClearing] = useState(false);
  
  // Overview card collapsed state with localStorage persistence
  const [overviewCardCollapsed, setOverviewCardCollapsed] = useState(() => {
    const saved = localStorage.getItem('contentOverviewCardCollapsed');
    return saved !== null ? saved === 'true' : false; // Default to expanded
  });
  const selectedSiteObj = sites.find(site => site.siteId === selectedSite);
  const showGa4Warning = Boolean(selectedSiteObj && !selectedSiteObj.analyticsPropertyId);
  const { user } = useAuth();
  const isDemoUser = user?.email === 'demo@example.com';
  const showGscWarning = Boolean(selectedSiteObj && !selectedSiteObj.searchConsoleUrl && !isDemoUser);
  const planRetentionDays = overview?.plan?.dataRetentionDays;
  const siteProfile = overview?.siteProfile;
  useEffect(() => {
    setRssInput(siteProfile?.rssFeedUrl || '');
  }, [siteProfile?.rssFeedUrl]);

  // Persist overview card collapsed state
  useEffect(() => {
    localStorage.setItem('contentOverviewCardCollapsed', overviewCardCollapsed.toString());
  }, [overviewCardCollapsed]);


  // Convert dateRange (number or object) to API format string
  const getDateRangeString = useCallback((range) => {
    if (typeof range === 'object' && range?.custom) {
      // Custom range: send actual dates in format "YYYY-MM-DD,YYYY-MM-DD"
      const start = new Date(range.startDate);
      const end = new Date(range.endDate);
      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];
      return `${startStr},${endStr}`;
    }
    if (range === 0) return 'today';
    if (range === 1) return 'yesterday';
    return `${range}d`;
  }, []);

  const primaryCategory = useMemo(() => {
    const value = siteProfile?.primaryCategory;
    return isCategoryAllowed(value) ? value : null;
  }, [siteProfile]);

  const secondaryCategories = useMemo(() => {
    if (!siteProfile || !Array.isArray(siteProfile.secondaryCategories)) {
      return [];
    }
    return siteProfile.secondaryCategories
      .filter(Boolean)
      .filter(category => isCategoryAllowed(category));
  }, [siteProfile]);

  const socialProfiles = useMemo(() => {
    if (!siteProfile || !Array.isArray(siteProfile.socialProfiles)) {
      return [];
    }

    const seen = new Set();
    return siteProfile.socialProfiles
      .map(profile => {
        if (!profile || !profile.url) {
          return null;
        }
        const normalizedUrl = profile.url.trim();
        if (!normalizedUrl) {
          return null;
        }
        const key = normalizedUrl.toLowerCase();
        if (seen.has(key)) {
          return null;
        }
        seen.add(key);
        const platform = profile.platform?.trim().toLowerCase() || '';
        return {
          platform,
          url: normalizedUrl,
          label: formatPlatformLabel(platform) || normalizedUrl
        };
      })
      .filter(Boolean);
  }, [siteProfile]);

  const hasSiteSummaryContent = useMemo(() => {
    if (!siteProfile) {
      return false;
    }
    return Boolean(
      siteProfile.description ||
      siteProfile.logoUrl ||
      primaryCategory ||
      secondaryCategories.length > 0 ||
      socialProfiles.length > 0
    );
  }, [primaryCategory, secondaryCategories, siteProfile, socialProfiles]);

  const filterSections = useCallback((list = []) => {
    return list.filter(section => {
      if (!section || !section.name) {
        return false;
      }
      return isCategoryAllowed(section.name);
    });
  }, []);

  const getPageTitle = useCallback((page) => {
    if (!page) {
      return 'Untitled';
    }

    const title = page.title?.trim();
    if (title) {
      return title;
    }

    const h1 = page.h1?.trim();
    if (h1) {
      return h1;
    }

    if (page.url) {
      try {
        const urlObj = new URL(page.url);
        const segments = urlObj.pathname.split('/').filter(Boolean);
        if (segments.length > 0) {
          const rawSegment = decodeURIComponent(segments[segments.length - 1]);
          const readable = rawSegment.replace(/[-_]+/g, ' ');
          return readable.charAt(0).toUpperCase() + readable.slice(1);
        }
        return urlObj.hostname;
      } catch (error) {
        return page.url;
      }
    }

    return 'Untitled';
  }, []);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data.sites);
      if (response.data.sites.length > 0) {
        setSelectedSite(response.data.sites[0].siteId);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAllData = useCallback(async () => {
    if (!selectedSite) return;
    
    try {
      setLoading(true);
      const [overviewRes, sectionsRes, pagesRes] = await Promise.all([
        axios.get(`/api/content/overview?siteId=${selectedSite}&range=${getDateRangeString(dateRange)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`/api/content/sections?siteId=${selectedSite}&range=${getDateRangeString(dateRange)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        (async () => {
          const params = new URLSearchParams({
            siteId: selectedSite,
            range: getDateRangeString(dateRange),
            sort: sortConfig.key,
            sortDir: sortConfig.direction
          });
          if (selectedSection !== 'all') {
            params.set('section', selectedSection);
          }
          return axios.get(`/api/content/pages?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        })()
      ]);

      if (overviewRes.data.ok) {
        const overviewData = overviewRes.data.data;
        setOverview(overviewData);
        
        // Check if there's truly no data (no pages, no pageviews, no sessions)
        const hasNoData = (!overviewData.totalPages || overviewData.totalPages === 0) &&
                          (!overviewData.totalPageviews || overviewData.totalPageviews === 0) &&
                          (!overviewData.totalSessions || overviewData.totalSessions === 0);
        
        if (hasNoData) {
          setNoDataMessage('No content data available. Click "Refresh Data" to run a scan and sync content data.');
        } else {
          setNoDataMessage('');
        }
      } else {
        setOverview(null);
        setNoDataMessage('No content data available. Click "Refresh Data" to run a scan and sync content data.');
      }
      
      if (sectionsRes.data.ok) {
        const sectionsData = filterSections(sectionsRes.data.data);
        setSections(sectionsData);
        // If no sections and no overview data, ensure no data message is set
        if (sectionsData.length === 0 && !overviewRes.data.ok) {
          setNoDataMessage('No content data available. Click "Refresh Data" to run a scan and sync content data.');
        }
      } else {
        setSections([]);
      }

      // Fetch queries if in queries view mode
      if (viewMode === 'queries') {
        try {
          const queriesParams = new URLSearchParams({
            siteId: selectedSite,
            range: getDateRangeString(dateRange),
            sort: sortConfig.key,
            sortDir: sortConfig.direction
          });
          const queriesRes = await axios.get(`/api/content/queries?${queriesParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (queriesRes.data.ok) {
            setQueries(queriesRes.data.data || []);
          } else {
            setQueries([]);
          }
        } catch (error) {
          console.error('Error fetching queries:', error);
          setQueries([]);
        }
      }
      
      if (pagesRes.data.ok) {
        const pagesData = pagesRes.data.data;
        setPages(pagesData);
        // If no pages and no overview data, ensure no data message is set
        if (pagesData.length === 0 && !overviewRes.data.ok) {
          setNoDataMessage('No content data available. Click "Refresh Data" to run a scan and sync content data.');
        }
      } else {
        setPages([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Only set error message for actual errors, not for "no data" scenarios
      if (error.response?.status === 401) {
        // Authentication errors should still be shown
      } else if (error.response?.status !== 404) {
        // For 404 or other "no data" scenarios, show the scan message
        setNoDataMessage('No content data available. Click "Refresh Data" to run a scan and sync content data.');
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange, filterSections, selectedSection, selectedSite, sortConfig, token, getDateRangeString, viewMode]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const checkSyncStatus = useCallback(async () => {
    if (!selectedSite) return;

    try {
      const response = await axios.get(
        `/api/analytics-data/${selectedSite}/sync-status?siteId=${selectedSite}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error checking sync status:', error);
      setSyncStatus({ canSync: true });
    }
  }, [selectedSite, token]);

  const formatMinutesToHours = useCallback((minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) {
      return null;
    }
    const totalMinutes = Math.max(0, Math.round(minutes));
    if (totalMinutes < 60) return null;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (mins === 0) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
  }, []);

  useEffect(() => {
    if (selectedSite) {
      fetchAllData();
    }
  }, [fetchAllData, selectedSite, dateRange]);

  useEffect(() => {
    if (selectedSite) {
      checkSyncStatus();
    }
  }, [selectedSite, checkSyncStatus]);

  const handleDateRangeChange = useCallback((newRange) => {
    setDateRange(newRange);
  }, []);

  useEffect(() => {
    if (selectedSection === 'all') {
      return;
    }
    const sectionExists = sections.some(section => section.sectionKey === selectedSection);
    if (!sectionExists) {
      setSelectedSection('all');
    }
  }, [sections, selectedSection]);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'desc' ? 'asc' : 'desc'
        };
      }
      return { key, direction: 'desc' };
    });
  }, []);

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return '↕';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  useEffect(() => {
    if (!planRetentionDays) {
      return;
    }
    // Check if current date range exceeds plan limits
    let currentDays = 30; // default
    if (typeof dateRange === 'object' && dateRange?.custom) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      currentDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    } else if (typeof dateRange === 'number') {
      if (dateRange === 0 || dateRange === 1) {
        currentDays = 1; // Today or Yesterday
      } else {
        currentDays = dateRange;
      }
    }
    
    if (currentDays <= planRetentionDays) {
      return;
    }
    
    // If exceeds plan limit, set to max allowed (30 days or plan limit, whichever is smaller)
    const maxAllowed = Math.min(30, planRetentionDays);
    if (dateRange !== maxAllowed) {
      setDateRange(maxAllowed);
    }
  }, [dateRange, planRetentionDays]);

  const saveRssFeed = useCallback(
    async (value) => {
      if (!selectedSite) return;
      try {
        setSavingRss(true);
        const response = await axios.post(
          '/api/content/site-profile/rss',
          {
            siteId: selectedSite,
            rssFeedUrl: value?.trim() || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.ok) {
          await fetchAllData();
          setIsEditingRss(false);
          const nextValue = response.data?.siteProfile?.rssFeedUrl || '';
          setRssInput(nextValue);
          const message =
            response.data?.message ||
            (nextValue ? 'RSS feed saved' : 'RSS feed cleared');
          alert(message);
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to save RSS feed';
        alert(message);
      } finally {
        setSavingRss(false);
      }
    },
    [fetchAllData, selectedSite, token]
  );
  
  const formatWarnings = useCallback((warnings = []) => {
    const filtered = (warnings || []).filter(Boolean);
    return filtered.length ? `\n\nWarnings:\n${filtered.join('\n')}` : '';
  }, []);

  // Animate progress bar while syncing - loops continuously
  useEffect(() => {
    if (!syncing) {
      setSyncProgress(0);
      setSyncError(null);
      return;
    }

    // Start progress animation that loops continuously
    const progressInterval = setInterval(() => {
      setSyncProgress(prev => {
        // Increment by 2% every 500ms
        const next = prev + 2;
        // When we reach 100%, reset to 0 to loop again
        return next >= 100 ? 0 : next;
      });
    }, 500);

    return () => clearInterval(progressInterval);
  }, [syncing]);

  const handleSync = async () => {
    if (!selectedSite || syncing) return;
    
    // Check sync status before attempting sync
    if (syncStatus && !syncStatus.canSync) {
      const minutesText = formatMinutesToHours(syncStatus.minutesRemaining) || `${syncStatus.minutesRemaining} minutes`;
      alert(`Please wait ${minutesText} before syncing again.`);
      return;
    }

    // Show notice only when fetching more than 90 days of data
    // Check the actual date range being synced, not the plan retention days
    // The backend will determine the actual sync range based on incremental sync logic
    // For now, we'll skip the warning since incremental sync handles this efficiently

    try {
      setSyncing(true);
      setSyncError(null);
      setSyncProgress(0);
      const { data } = await axios.post('/api/content/sync', { siteId: selectedSite }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchAllData();
      await checkSyncStatus(); // Refresh sync status after successful sync
      // Stop syncing after data refresh completes
      setSyncing(false);
      setSyncProgress(0);
      const message = data?.message || 'Content sync completed successfully!';
      alert(message + formatWarnings(data?.warnings));
    } catch (error) {
      console.error('Sync error:', error);
      setSyncing(false);
      setSyncProgress(0);
      
      // Set sync error message
      let errorMessage = 'Sync failed. Please try again.';
      if (error.response?.status === 429) {
        await checkSyncStatus(); // Refresh sync status
        const minutesText = formatMinutesToHours(error.response?.data?.minutesRemaining) || `${error.response?.data?.minutesRemaining} minutes`;
        errorMessage = `Rate limit reached. Please wait ${minutesText} before syncing again.`;
      } else if (error.response?.status === 403 && error.response?.data?.requiresReconnect) {
        setShowGA4Modal(true);
        errorMessage = error.response?.data?.message || 'Your Google Analytics connection needs to be refreshed. Please reconnect your Google Analytics account to grant the required permissions.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message + formatWarnings(error.response?.data?.warnings);
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSyncError(errorMessage);
    }
  };

  const handlePageClick = (contentId) => {
    navigate(`/page/${contentId}?siteId=${selectedSite}`);
  };

  const handleClearData = async () => {
    if (!selectedSite || clearing) return;

    if (!window.confirm('Are you sure you want to clear all content data for this site? This action cannot be undone.')) {
      return;
    }

    try {
      setClearing(true);
      setSyncError(null);
      
      await axios.post(`/api/content/clear-data`, {
        siteId: selectedSite
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear the current data display
      setOverview(null);
      setSections([]);
      setPages([]);
      setQueries([]);
      setNoDataMessage('No content data available. Click "Refresh Data" to run a scan and sync content data.');
      
      alert('✅ Content data cleared successfully! Click "Refresh Data" to get fresh data.');
      
    } catch (error) {
      console.error('Error clearing data:', error);
      setSyncError('Failed to clear content data. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  const lastRefreshedLabel = syncStatus?.lastSync
    ? new Date(syncStatus.lastSync).toLocaleString()
    : null;
  const lastSyncText = syncing
    ? 'Refreshing now...'
    : lastRefreshedLabel
    ? `Last refreshed: ${lastRefreshedLabel}`
    : '';

  const siteDisplayName = selectedSiteObj?.name || siteProfile?.homepageTitle || 'Site Overview';
  const fallbackInitial = siteDisplayName ? siteDisplayName.charAt(0).toUpperCase() : '?';

  if (loading && !overview) {
    return (
      <div className="content-page">
        <main className="content-container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading content data...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="content-page">
      <header className="content-header">
        <div className="header-top">
          <div className="header-left">
            <div className="header-title-section">
              <h1>Content Intelligence</h1>
            </div>
            {selectedSite && (
              <div className="site-selector">
                <select
                  value={selectedSite || ''}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  className="site-dropdown"
                  disabled={!sites.length}
                >
                  {sites.map(site => (
                    <option key={site.siteId} value={site.siteId}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <DateRangeSelector
              timeRange={dateRange}
              onTimeRangeChange={handleDateRangeChange}
              maxDays={planRetentionDays}
            />
          </div>
          
          <div className="header-right">
            {selectedSite && (
              <div className="sync-controls">
                <div className="sync-buttons">
                  <div className="sync-primary">
                    <button
                      onClick={handleSync}
                      disabled={!selectedSite || syncing || (syncStatus && !syncStatus.canSync)}
                      className="btn-sync"
                      type="button"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0115.5-8.5l4 4M22 12.5a10 10 0 01-15.5 8.5l-4-4" />
                      </svg>
                      {syncing ? 'Syncing...' : 'Refresh Data'}
                    </button>
                    {syncing && (
                      <div className="sync-progress-bar">
                        <div 
                          className="sync-progress-fill" 
                          style={{ width: `${syncProgress}%` }}
                        ></div>
                      </div>
                    )}
                    {lastSyncText && (
                      <span className="last-updated" aria-live="polite">
                        {lastSyncText}
                      </span>
                    )}
                  </div>
                  <button 
                    className="btn-clear-data"
                    onClick={handleClearData}
                    disabled={clearing}
                    title="Clear all content data for this site"
                    type="button"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M10 11v6M14 11v6" />
                    </svg>
                    {clearing ? 'Clearing...' : 'Clear Data'}
                  </button>
                  {syncError && (
                    <div className="sync-error">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      {syncError}
                    </div>
                  )}
                  {syncStatus && !syncStatus.canSync && (
                    <div className="sync-info">
                      ⏱ Next sync in {formatMinutesToHours(syncStatus.minutesRemaining) || `${syncStatus.minutesRemaining} minutes`}
                      {syncStatus.nextAvailableAt && ` (${new Date(syncStatus.nextAvailableAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="content-container">

        {showGa4Warning && (
          <div className="content-warning ga4-warning" role="status">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
              <path d="M12 8v5" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="12" cy="16" r="0.5" fill="currentColor" strokeWidth="1.5" />
            </svg>
            <div className="content-warning-text">
              <strong>Google Analytics not connected</strong>
              <p>Connect GA4 to unlock engagement metrics, top pages, and insights for this site.</p>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowGA4Modal(true);
              }}
            >
              Connect Google Analytics
            </button>
          </div>
        )}

        {showGscWarning && (
          <div className="content-warning gsc-warning" role="status">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
              <path d="M12 8v5" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="12" cy="16" r="0.5" fill="currentColor" strokeWidth="1.5" />
            </svg>
            <div className="content-warning-text">
              <strong>Google Search Console not connected</strong>
              <p>Connect Search Console from the Sites page to unlock search impressions, clicks, and CTR analytics.</p>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/sites?connectGSC=1&siteId=${selectedSite || ''}`)}
            >
              Connect Search Console
            </button>
          </div>
        )}

        {noDataMessage && !overview && (
          <div className="no-data-message" style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            background: '#f8f9fa', 
            borderRadius: '8px', 
            marginBottom: '2rem',
            border: '1px solid #dee2e6'
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: '0 auto 1rem', color: '#6c757d' }}>
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
            <h3 style={{ marginBottom: '0.5rem', color: '#495057' }}>No Content Data Available</h3>
            <p style={{ marginBottom: '1rem', color: '#6c757d' }}>{noDataMessage}</p>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSync}
              disabled={syncing || (syncStatus && !syncStatus.canSync)}
            >
              {syncing ? 'Syncing...' : 'Refresh Data'}
            </button>
          </div>
        )}

        {overview && (
          <>
            <div className="site-summary-card data-panel">
              <div className="site-summary-header">
                <button
                  className="toggle-overview-card"
                  onClick={() => setOverviewCardCollapsed(!overviewCardCollapsed)}
                  aria-label={overviewCardCollapsed ? 'Expand overview card' : 'Collapse overview card'}
                  type="button"
                >
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    style={{ transform: overviewCardCollapsed ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                <div className="site-summary-logo" aria-hidden={!siteProfile?.logoUrl}>
                  {siteProfile?.logoUrl ? (
                    <img src={siteProfile.logoUrl} alt={`${siteDisplayName} logo`} />
                  ) : (
                    <span className="site-summary-logo-fallback">{fallbackInitial}</span>
                  )}
                </div>
                <div className="site-summary-meta">
                  <div className="site-summary-title-row">
                    <h2>{siteDisplayName}</h2>
                    {siteProfile?.homepageUrl && (
                      <a href={siteProfile.homepageUrl} target="_blank" rel="noopener noreferrer">
                        Visit site
                      </a>
                    )}
                  </div>
                  {hasSiteSummaryContent ? (
                    <p>{siteProfile?.description || 'We detected key categories for this site. Run another crawl to enrich the summary.'}</p>
                  ) : (
                    <p className="site-summary-placeholder">Run a site crawl to generate a rich summary and category breakdown.</p>
                  )}
                </div>
              </div>
              {!overviewCardCollapsed && (
                <>
                  {(primaryCategory || secondaryCategories.length > 0) && (
                    <div className="site-summary-categories">
                      {primaryCategory && (
                        <div className="category-group">
                          <span className="category-label">Primary category</span>
                          <span className="category-chip category-primary">{primaryCategory}</span>
                        </div>
                      )}
                      {secondaryCategories.length > 0 && (
                        <div className="category-group">
                          <span className="category-label">Secondary categories</span>
                          <div className="category-chip-collection">
                            {secondaryCategories.map(category => (
                              <span key={category} className="category-chip">
                                {category}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="site-summary-rss">
                    <span className="category-label">RSS Feed</span>
                    {siteProfile?.rssFeedUrl ? (
                      <a
                        href={siteProfile.rssFeedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rss-feed-link"
                      >
                        View RSS Feed
                      </a>
                    ) : (
                      <span className="rss-feed-status">Not detected</span>
                    )}
                    <button
                      type="button"
                      className="btn-tertiary"
                      onClick={() => {
                        setIsEditingRss((prev) => !prev);
                        setRssInput(siteProfile?.rssFeedUrl || '');
                      }}
                    >
                      {siteProfile?.rssFeedUrl ? 'Edit' : 'Add RSS Feed'}
                    </button>
                  </div>
                  {isEditingRss && (
                    <div className="rss-edit-form">
                      <input
                        type="url"
                        value={rssInput}
                        onChange={(e) => setRssInput(e.target.value)}
                        placeholder="https://example.com/feed.xml"
                      />
                      <div className="rss-edit-actions">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => saveRssFeed(rssInput)}
                          disabled={savingRss}
                        >
                          {savingRss ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="btn-tertiary"
                          onClick={() => {
                            setIsEditingRss(false);
                            setRssInput(siteProfile?.rssFeedUrl || '');
                          }}
                        >
                          Cancel
                        </button>
                        {siteProfile?.rssFeedUrl && (
                          <button
                            type="button"
                            className="btn-link danger"
                            onClick={() => saveRssFeed('')}
                            disabled={savingRss}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {socialProfiles.length > 0 && (
                    <div className="site-summary-social">
                      <span className="category-label">Social channels</span>
                      <div className="social-links">
                        {socialProfiles.map(profile => (
                          <a
                            key={profile.url}
                            href={profile.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {profile.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {overview?.demographics && (
                    <div className="site-summary-demographics">
                      {(() => {
                  const hasAgeData = overview.demographics.ageGroups && Object.keys(overview.demographics.ageGroups).length > 0;
                  const hasGenderData = overview.demographics.gender && Object.keys(overview.demographics.gender).length > 0;
                  const hasAffinityData = overview.demographics.topAffinities && overview.demographics.topAffinities.length > 0;
                  const hasAnyData = hasAgeData || hasGenderData || hasAffinityData;

                  if (!hasAnyData) {
                    const ga4PropertyId = selectedSiteObj?.analyticsPropertyId;
                    const googleSignalsUrl = ga4PropertyId 
                      ? `https://analytics.google.com/analytics/web/#/p${ga4PropertyId}/admin/settings/data-collection`
                      : 'https://support.google.com/analytics/answer/9445345';
                    
                    return (
                      <div className="demographic-empty-state">
                        <span className="category-label">Demographics</span>
                        <div className="demographic-empty-message">
                          <p>No demographic data available. This may be due to:</p>
                          <ul>
                            <li>Google Signals not being enabled in your GA4 property</li>
                            <li>Insufficient data to meet privacy thresholds</li>
                            <li>Users not consenting to ads personalization</li>
                          </ul>
                          {ga4PropertyId && (
                            <a 
                              href={googleSignalsUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="demographic-enable-link"
                            >
                              Enable Google Signals in GA4 →
                            </a>
                          )}
                          {!ga4PropertyId && (
                            <a 
                              href={googleSignalsUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="demographic-enable-link"
                            >
                              Learn about Google Signals →
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <>
                      {hasAgeData && (
                        <div className="demographic-group">
                          <span className="category-label">Age Groups</span>
                          <div className="demographic-chips">
                            {Object.entries(overview.demographics.ageGroups)
                              .sort((a, b) => (b[1]?.users || 0) - (a[1]?.users || 0))
                              .map(([ageGroup, data]) => (
                                <span key={ageGroup} className="demographic-chip">
                                  {ageGroup}: {friendlyNumber(data.users)} users
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                      {hasGenderData && (
                        <div className="demographic-group">
                          <span className="category-label">Gender</span>
                          <div className="demographic-chips">
                            {Object.entries(overview.demographics.gender)
                              .sort((a, b) => (b[1]?.users || 0) - (a[1]?.users || 0))
                              .map(([gender, data]) => (
                                <span key={gender} className="demographic-chip">
                                  {gender}: {friendlyNumber(data.users)} users
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                      {hasAffinityData && (
                        <div className="demographic-group">
                          <span className="category-label">Top Interests</span>
                          <div className="demographic-chips">
                            {overview.demographics.topAffinities.map((affinity, idx) => (
                              <span key={idx} className="demographic-chip">
                                {affinity.category}: {friendlyNumber(affinity.users)} users
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                        </>
                      );
                    })()}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="overview-cards">
              <div className="card">
                <h3>Total Pages</h3>
                <div className="metric-value">{overview.totalPages || 0}</div>
                <span className="metric-caption">Indexed content</span>
              </div>
              <div className="card">
                <h3>New/Updated Pages</h3>
                <div className="metric-value">{overview.newUpdatedPages?.toLocaleString() || 0}</div>
                <span className="metric-caption">Updated in this period</span>
              </div>
              <div className="card">
                <h3>AdShop Authority</h3>
                <div className="metric-value">{overview.seoMetrics?.adShopAuthority || '—'}</div>
                <span className="metric-caption">Authority score (0-100)</span>
              </div>
              <div className="card">
                <h3>Backlinks</h3>
                <div className="metric-value">{overview.seoMetrics?.referringDomains?.toLocaleString() || '—'}</div>
                <span className="metric-caption">Referring domains</span>
              </div>
              <div className="card">
                <h3>Keywords</h3>
                <div className="metric-value">{overview.seoMetrics?.organicKeywords?.toLocaleString() || '—'}</div>
                <span className="metric-caption">Organic queries</span>
              </div>
            </div>

            <div className="sections-panel-horizontal data-panel">
              <div className="panel-header">
                <h2>Sections</h2>
                <select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  className="section-filter content-select"
                >
                  <option value="all">All Sections</option>
                  {sections.map(section => (
                    <option key={section.sectionId} value={section.sectionKey}>
                      {section.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sections-list-horizontal">
                {sections.map(section => {
                  const isActive = selectedSection === section.sectionKey;
                  return (
                    <button
                      key={section.sectionId}
                      className={`section-card ${isActive ? 'active' : ''}`}
                      onClick={() => setSelectedSection(section.sectionKey)}
                      type="button"
                    >
                      <h4>{section.name}</h4>
                      <div className="section-metrics">
                        <span>{friendlyNumber(section.metrics.pageviews)} views</span>
                        <span>{formatDuration(section.metrics.avgEngagementSeconds)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pages-table data-panel">
              <div className="panel-header">
                <div className="panel-header-left">
                  <h2>{viewMode === 'pages' ? 'Pages' : 'Queries'}</h2>
                  <span className="panel-subtitle">Click a row to explore performance details</span>
                </div>
                <div className="view-mode-toggle">
                  <button
                    type="button"
                    className={`toggle-btn ${viewMode === 'pages' ? 'active' : ''}`}
                    onClick={() => setViewMode('pages')}
                  >
                    Pages
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${viewMode === 'queries' ? 'active' : ''}`}
                    onClick={() => setViewMode('queries')}
                  >
                    Queries
                  </button>
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={`table-sort-button ${sortConfig.key === (viewMode === 'pages' ? 'title' : 'query') ? `sorted-${sortConfig.direction}` : ''}`}
                        onClick={() => handleSort(viewMode === 'pages' ? 'title' : 'query')}
                      >
                        <span>{viewMode === 'pages' ? 'Title' : 'Query'}</span>
                        <span className="sort-indicator">{getSortIndicator(viewMode === 'pages' ? 'title' : 'query')}</span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`table-sort-button ${sortConfig.key === (viewMode === 'pages' ? 'pageviews' : 'clicks') ? `sorted-${sortConfig.direction}` : ''}`}
                        onClick={() => handleSort(viewMode === 'pages' ? 'pageviews' : 'clicks')}
                      >
                        <span>{viewMode === 'pages' ? 'Pageviews' : 'Clicks'}</span>
                        <span className="sort-indicator">{getSortIndicator(viewMode === 'pages' ? 'pageviews' : 'clicks')}</span>
                      </button>
                    </th>
                    <th>
                      {viewMode === 'pages' ? (
                        <button
                          type="button"
                          className={`table-sort-button ${sortConfig.key === 'engagementRate' ? `sorted-${sortConfig.direction}` : ''}`}
                          onClick={() => handleSort('engagementRate')}
                        >
                          <span className="header-label-with-tooltip">
                            <span>Engagement Rate</span>
                            <Tooltip content="Percentage of sessions on this page that were considered engaged (lasting over 10 seconds, having 2+ pageviews, or including a conversion).">
                              <div className="tooltip-icon" role="img" aria-label="Engagement rate explanation">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M12 16v-4M12 8h.01" />
                                </svg>
                              </div>
                            </Tooltip>
                          </span>
                          <span className="sort-indicator">{getSortIndicator('engagementRate')}</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`table-sort-button ${sortConfig.key === 'ctr' ? `sorted-${sortConfig.direction}` : ''}`}
                          onClick={() => handleSort('ctr')}
                        >
                          <span>CTR</span>
                          <span className="sort-indicator">{getSortIndicator('ctr')}</span>
                        </button>
                      )}
                    </th>
                    <th>
                      <button
                        type="button"
                        className={`table-sort-button ${sortConfig.key === (viewMode === 'pages' ? 'avgEngagementSeconds' : 'impressions') ? `sorted-${sortConfig.direction}` : ''}`}
                        onClick={() => handleSort(viewMode === 'pages' ? 'avgEngagementSeconds' : 'impressions')}
                      >
                        <span>{viewMode === 'pages' ? 'Avg Engagement Time' : 'Impressions'}</span>
                        <span className="sort-indicator">{getSortIndicator(viewMode === 'pages' ? 'avgEngagementSeconds' : 'impressions')}</span>
                      </button>
                    </th>
                    {viewMode === 'pages' ? (
                      <th>
                        <button
                          type="button"
                          className={`table-sort-button ${sortConfig.key === 'scrollDepth90' ? `sorted-${sortConfig.direction}` : ''}`}
                          onClick={() => handleSort('scrollDepth90')}
                        >
                          <span className="header-label-with-tooltip">
                            <span>90% Scroll</span>
                            <Tooltip content="Percentage of pageviews where users scrolled to 90% of the page depth. Automatically tracked by GA4 Enhanced Measurement.">
                              <div className="tooltip-icon" role="img" aria-label="Scroll depth explanation">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M12 16v-4M12 8h.01" />
                                </svg>
                              </div>
                            </Tooltip>
                          </span>
                          <span className="sort-indicator">{getSortIndicator('scrollDepth90')}</span>
                        </button>
                      </th>
                    ) : (
                      <>
                        <th>
                          <button
                            type="button"
                            className={`table-sort-button ${sortConfig.key === 'position' ? `sorted-${sortConfig.direction}` : ''}`}
                            onClick={() => handleSort('position')}
                          >
                            <span>Position</span>
                            <span className="sort-indicator">{getSortIndicator('position')}</span>
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className={`table-sort-button ${sortConfig.key === 'pageviews' ? `sorted-${sortConfig.direction}` : ''}`}
                            onClick={() => handleSort('pageviews')}
                          >
                            <span>Pageviews</span>
                            <span className="sort-indicator">{getSortIndicator('pageviews')}</span>
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className={`table-sort-button ${sortConfig.key === 'engagementRate' ? `sorted-${sortConfig.direction}` : ''}`}
                            onClick={() => handleSort('engagementRate')}
                          >
                            <span className="header-label-with-tooltip">
                              <span>Engagement Rate</span>
                              <Tooltip content="Percentage of sessions on pages that received clicks from this query that were considered engaged.">
                                <div className="tooltip-icon" role="img" aria-label="Engagement rate explanation">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4M12 8h.01" />
                                  </svg>
                                </div>
                              </Tooltip>
                            </span>
                            <span className="sort-indicator">{getSortIndicator('engagementRate')}</span>
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className={`table-sort-button ${sortConfig.key === 'avgEngagementSeconds' ? `sorted-${sortConfig.direction}` : ''}`}
                            onClick={() => handleSort('avgEngagementSeconds')}
                          >
                            <span>Avg Engagement Time</span>
                            <span className="sort-indicator">{getSortIndicator('avgEngagementSeconds')}</span>
                          </button>
                        </th>
                        <th>
                          <button
                            type="button"
                            className={`table-sort-button ${sortConfig.key === 'scrollDepth90' ? `sorted-${sortConfig.direction}` : ''}`}
                            onClick={() => handleSort('scrollDepth90')}
                          >
                            <span className="header-label-with-tooltip">
                              <span>90% Scroll</span>
                              <Tooltip content="Percentage of pageviews from pages that received clicks from this query where users scrolled to 90% of the page depth.">
                                <div className="tooltip-icon" role="img" aria-label="Scroll depth explanation">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 16v-4M12 8h.01" />
                                  </svg>
                                </div>
                              </Tooltip>
                            </span>
                            <span className="sort-indicator">{getSortIndicator('scrollDepth90')}</span>
                          </button>
                        </th>
                      </>
                    )}
                    {viewMode === 'pages' && (
                      <th>
                        <button
                          type="button"
                          className={`table-sort-button ${sortConfig.key === 'impressions' ? `sorted-${sortConfig.direction}` : ''}`}
                          onClick={() => handleSort('impressions')}
                        >
                          <span>Search Impressions</span>
                          <span className="sort-indicator">{getSortIndicator('impressions')}</span>
                        </button>
                      </th>
                    )}
                    {viewMode === 'pages' && (
                      <th>
                        <button
                          type="button"
                          className={`table-sort-button ${sortConfig.key === 'ctr' ? `sorted-${sortConfig.direction}` : ''}`}
                          onClick={() => handleSort('ctr')}
                        >
                          <span>CTR</span>
                          <span className="sort-indicator">{getSortIndicator('ctr')}</span>
                        </button>
                      </th>
                    )}
                    {viewMode === 'pages' && (
                      <th>
                        <button
                          type="button"
                          className={`table-sort-button ${sortConfig.key === 'healthScore' ? `sorted-${sortConfig.direction}` : ''}`}
                          onClick={() => handleSort('healthScore')}
                        >
                          <span className="header-label-with-tooltip">
                            <span>SEO Health</span>
                            <Tooltip content="Composite 0–100 score combining engagement time, sessions, Google Search CTR, freshness, keyword/content matching, and query relevance. Higher values indicate pages that are well-optimized, current, engaging, and aligned with search intent.">
                              <div className="tooltip-icon" role="img" aria-label="SEO Health score explanation">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <path d="M12 16v-4M12 8h.01" />
                                </svg>
                              </div>
                            </Tooltip>
                          </span>
                          <span className="sort-indicator">{getSortIndicator('healthScore')}</span>
                        </button>
                      </th>
                    )}
                    {viewMode === 'pages' && <th>Last Updated</th>}
                  </tr>
                </thead>
                <tbody>
                  {viewMode === 'pages' ? (
                    <>
                  {pages.length === 0 && (
                    <tr className="empty-row">
                      <td colSpan={viewMode === 'pages' ? 9 : 8}>No pages available for this selection.</td>
                    </tr>
                  )}
                      {pages.map(page => {
                    const metrics = page.metrics || {};
                    const ctrValue = typeof metrics.ctr === 'number' ? `${(metrics.ctr * 100).toFixed(1)}%` : '—';
                    const healthScore = typeof metrics.healthScore === 'number' ? metrics.healthScore : '—';
                    const healthBucket = typeof metrics.healthScore === 'number'
                      ? Math.min(2, Math.floor(metrics.healthScore / 33))
                      : 'na';
                    const engagementRate = Number.isFinite(metrics.engagementRate)
                      ? metrics.engagementRate
                      : null;
                    const engagementDisplay = engagementRate !== null
                      ? formatPercent(engagementRate)
                      : '—';
                    const engagementBadgeClass = getEngagementBadgeClass(engagementRate);
                    const engagedSessions = metrics.engagedSessions || 0;
                    
                    // Format date
                    const formatDate = (date) => {
                      if (!date) return '—';
                      try {
                        const d = new Date(date);
                        if (isNaN(d.getTime())) return '—';
                        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                      } catch (e) {
                        return '—';
                      }
                    };
                    const lastUpdated = page.dateModified || page.datePublished || null;
                    const lastUpdatedDisplay = formatDate(lastUpdated);

                    return (
                      <tr 
                        key={page.contentId} 
                        onClick={() => handlePageClick(page.contentId)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          const url = `/page/${page.contentId}?siteId=${selectedSite}`;
                          window.open(url, '_blank');
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{getPageTitle(page)}</td>
                        <td>{friendlyNumber(metrics.pageviews)}</td>
                        <td>
                          <Tooltip content={`${friendlyNumber(engagedSessions)} engaged sessions`}>
                            <span className={`engagement-badge engagement-${engagementBadgeClass}`}>
                              {engagementDisplay}
                            </span>
                          </Tooltip>
                        </td>
                        <td>{formatDuration(metrics.avgEngagementSeconds)}</td>
                        <td>
                          <Tooltip content={`${friendlyNumber(metrics.scrollDepth90 || 0)} out of ${friendlyNumber(metrics.pageviews || 0)} pageviews (${((metrics.scrollDepth90Percent || 0).toFixed(1))}%) reached 90% scroll depth`}>
                            <span className="scroll-depth-percentage">
                              {metrics.scrollDepth90Percent !== undefined && metrics.scrollDepth90Percent !== null
                                ? `${metrics.scrollDepth90Percent.toFixed(1)}%`
                                : '—'}
                            </span>
                          </Tooltip>
                        </td>
                        <td>{friendlyNumber(metrics.impressions)}</td>
                        <td>{ctrValue}</td>
                        <td>
                          <span className={`health-score health-${healthBucket}`}>
                            {healthScore}
                          </span>
                        </td>
                        <td>{lastUpdatedDisplay}</td>
                      </tr>
                    );
                      })}
                    </>
                  ) : (
                    <>
                      {queries.length === 0 && (
                        <tr className="empty-row">
                          <td colSpan={9}>No queries available for this selection.</td>
                        </tr>
                      )}
                      {queries.map(query => {
                        const ctrValue = typeof query.ctr === 'number' ? `${(query.ctr * 100).toFixed(1)}%` : '—';
                        const position = formatPosition(query.position);
                        const engagementRate = Number.isFinite(query.engagementRate)
                          ? query.engagementRate
                          : null;
                        const engagementDisplay = engagementRate !== null
                          ? formatPercent(engagementRate)
                          : '—';
                        const engagementBadgeClass = getEngagementBadgeClass(engagementRate);
                        const scrollDepth90Percent = typeof query.scrollDepth90Percent === 'number'
                          ? `${(query.scrollDepth90Percent * 100).toFixed(1)}%`
                          : '—';
                        
                        return (
                          <tr key={query.query || query._id}>
                            <td>{query.query || '—'}</td>
                            <td>{friendlyNumber(query.clicks || 0)}</td>
                            <td>
                              <span className={`engagement-badge engagement-neutral`}>
                                {ctrValue}
                              </span>
                            </td>
                            <td>{friendlyNumber(query.impressions || 0)}</td>
                            <td>{position}</td>
                            <td>{friendlyNumber(query.pageviews || 0)}</td>
                            <td>
                              <Tooltip content={`Aggregated from pages that received clicks from this query`}>
                                <span className={`engagement-badge engagement-${engagementBadgeClass}`}>
                                  {engagementDisplay}
                                </span>
                              </Tooltip>
                            </td>
                            <td>{formatDuration(query.avgEngagementSeconds || 0)}</td>
                            <td>
                              <Tooltip content={`Percentage of pageviews from pages that received clicks from this query where users scrolled to 90% depth`}>
                                <span className="scroll-depth-percentage">
                                  {scrollDepth90Percent}
                                </span>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
      
      {/* GA4 Connection Modal for Reconnection */}
      {selectedSiteObj && (
        <GA4ConnectionModal
          isOpen={showGA4Modal}
          onClose={() => {
            setShowGA4Modal(false);
          }}
          onConnect={async (property) => {
            try {
              // Update the site with new property
              await axios.post(
                `/api/sites/${selectedSiteObj.siteId}/analytics`,
                { propertyId: property.propertyId, propertyName: property.propertyName },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              // Update selected site in local state
              const updatedSite = { ...selectedSiteObj, analyticsPropertyId: property.propertyId, analyticsPropertyName: property.propertyName };
              setSites(sites.map(s => s.siteId === selectedSiteObj.siteId ? updatedSite : s));
              
              // Close modal and clear errors
              setShowGA4Modal(false);
              
              // Refresh data
              await fetchAllData();
              
              alert('Google Analytics reconnected successfully!');
            } catch (error) {
              console.error('Error reconnecting GA4:', error);
              alert('Failed to reconnect Google Analytics. Please try again.');
            }
          }}
          site={selectedSiteObj}
        />
      )}
      <FeedbackNotification />
    </div>
  );
};

export default Content;

